import { format, parseISO } from 'date-fns'
import { formatForTimezone } from '../../modules/utilities/dates.js'
import { displayNumber } from '../../modules/utilities/numbers.js'
import { displayReportResults } from '../../modules/ui.js'

const reportTemplate = document.getElementById('tmpl-report-originalLotTransfers')

export default class MTOriginalLotTransfersReport extends HTMLElement {
    constructor() {
        super()
        this.attachShadow({ mode: 'open' })
        this.shadowRoot.appendChild(reportTemplate.content.cloneNode(true))

        this.errorElement = this.shadowRoot.querySelector('#js-error-container')
        this.startDateElement = this.shadowRoot.querySelector('.js-summary-start')
        this.endDateElement = this.shadowRoot.querySelector('.js-summary-end')
        this.minHeadElement = this.shadowRoot.querySelector('.js-summary-min-head')
        this.maxHeadElement = this.shadowRoot.querySelector('.js-summary-max-head')
        this.transferredHeadElement = this.shadowRoot.querySelector('.js-summary-transferred-head')
        this.summaryListElement = this.shadowRoot.querySelector('#js-summary-list')
        this.printElement = this.shadowRoot.querySelector('#js-print-report')

        this.printElement.addEventListener(
            'click',
            () => {
                this.dispatchEvent(new CustomEvent('printReportEvent'))
            },
            false
        )
    }

    set data(value) {
        const startDate = document.querySelector('#startDate').value
        const endDate = document.querySelector('#endDate').value
        const minHead = document.querySelector('#minHead').value
        const maxHead = document.querySelector('#maxHead').value

        this._data = value
        this._results = this.sortLocationResults(value.reportData ?? [])
        this._summary = this.calculateSummary({
            startDate: new Date(parseISO(startDate)),
            endDate: new Date(parseISO(endDate)),
            minHead,
            maxHead,
        })

        if (this._data.hasError) {
            this.displayError()
        } else {
            this.displaySummary()
            displayReportResults(this, '#js-original-lot-transfers-results-container')
        }
    }

    displayError() {
        console.log(this._data.errorMessage)
        this.errorElement.innerText = this._data.errorMessage
    }

    calculateSummary({ startDate, endDate, minHead, maxHead }) {
        const rows = this._results.flatMap((r) => r.data)
        const sexTotals = new Map()
        const transferredHead = rows.reduce((total, row) => {
            const head = this.getNumber(row, this.headKeys())
            const sex = this.getValue(row, ['SEX'])

            if (sex !== '') {
                sexTotals.set(sex, (sexTotals.get(sex) ?? 0) + head)
            }

            return total + head
        }, 0)

        return {
            startDate,
            endDate,
            minHead,
            maxHead,
            transferredHead,
            sexTotals: Array.from(sexTotals)
                .map(([sex, head]) => ({ sex, head }))
                .sort((left, right) => left.sex.localeCompare(right.sex)),
        }
    }

    displaySummary() {
        this.startDateElement.innerText = format(this._summary.startDate, 'MM/dd/yyyy')
        this.endDateElement.innerText = format(this._summary.endDate, 'MM/dd/yyyy')
        this.minHeadElement.innerText = displayNumber(this._summary.minHead, 0)
        this.maxHeadElement.innerText = displayNumber(this._summary.maxHead, 0)
        this.transferredHeadElement.innerText = displayNumber(this._summary.transferredHead, 0)

        this._summary.sexTotals.forEach((sexTotal) => {
            this.summaryListElement.insertAdjacentHTML('beforeend', this.summaryTemplate(sexTotal))
        })
    }

    summaryTemplate(props) {
        return `
            <div class="summary-card">
                <dt class="summary-label">
                    ${props.sex} Head
                </dt>
                <dd class="summary-content">${displayNumber(props.head, 0)}</dd>
            </div>
        `
    }

    sortLocationResults(results) {
        return results
            .map((result) => ({
                ...result,
                data: this.sortRows(result.data ?? []),
            }))
            .sort((left, right) => left.locationName.localeCompare(right.locationName))
    }

    sortRows(rows) {
        return [...rows].sort((left, right) => {
            const leftDate = this.toSortableDate(this.getRawValue(left, this.dateKeys()))
            const rightDate = this.toSortableDate(this.getRawValue(right, this.dateKeys()))
            const dateComparison = this.compareValues(leftDate, rightDate)

            if (dateComparison !== 0) return dateComparison

            const srcLotComparison = this.getValue(left, this.srcLotKeys()).localeCompare(this.getValue(right, this.srcLotKeys()))
            if (srcLotComparison !== 0) return srcLotComparison

            const destLotComparison = this.getValue(left, this.destLotKeys()).localeCompare(this.getValue(right, this.destLotKeys()))
            if (destLotComparison !== 0) return destLotComparison

            return this.getValue(left, this.eventIdKeys()).localeCompare(this.getValue(right, this.eventIdKeys()))
        })
    }

    compareValues(left, right) {
        if (left < right) return -1
        if (left > right) return 1
        return 0
    }

    locationNoResultsTemplate(props) {
        return `
            <div class="mt-well mt-template-indent" data-location-id="${props.locationID}">
                <div class="mt-well--content">
                <h3 class="mt-report-title">${props.locationName}</h3>
                    <p>There are no results for '${props.locationName}' during this timeframe.</p>
                </div>
            </div>
        `
    }

    locationResultsTemplate(props) {
        const headTotal = props.data.reduce(
            (total, row) => total + this.getNumber(row, this.headKeys()),
            0
        )

        return `
            <div class="mt-well" data-location-id="${props.locationID}" data-collapsible-id="${props.locationID}" data-collapsed="false">
                <div class="mt-well--content">
                    <h3 class="mt-report-title">
                        <svg id="collapse-${props.locationID}" data-collapsed="false" class="h-3 w-3 md:h-6 md:w-6" fill="var(--color-action)" viewBox="-150 -350 900 900" height="1.5rem" aria-hidden="true" style="cursor:pointer;">
                            <path name="expand-icon"
                              d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm144 276c0 6.6-5.4 12-12 12h-92v92c0 6.6-5.4 12-12 12h-56c-6.6 0-12-5.4-12-12v-92h-92c-6.6 0-12-5.4-12-12v-56c0-6.6 5.4-12 12-12h92v-92c0-6.6 5.4-12 12-12h56c6.6 0 12 5.4 12 12v92h92c6.6 0 12 5.4 12 12v56z" />
                            <path name="collapse-icon"
                              d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zM124 296c-6.6 0-12-5.4-12-12v-56c0-6.6 5.4-12 12-12h264c6.6 0 12 5.4 12 12v56c0 6.6-5.4 12-12 12H124z" />
                        </svg>
                        ${props.locationName}
                    </h3>
                    <div class="mt-table--container">
                    <table class="js-report--table mt-table">
                <thead class="mt-table--head">
                    <tr>
                        <th scope="col" class="center date collapsed-hidden">
                            Date
                        </th>
                        <th scope="col" class="right head">
                            <abbr title="Total Head Transferred">Head</abbr>
                        </th>
                        <th scope="col" class="left sex collapsed-hidden">
                            <abbr title="Source Lot/Home Pen Sex">Sex</abbr>
                        </th>
                        <th scope="col" class="right dof collapsed-hidden">
                            <abbr title="Source Lot/Home Pen Days on Feed">DOF</abbr>
                        </th>
                        <th scope="col" class="left cause collapsed-hidden">
                            Cause
                        </th>
                        <th scope="col" class="left src-lot collapsed-hidden">
                            <abbr title="Source Lot">Src Lot</abbr>
                        </th>
                        <th scope="col" class="right src-owner-percentage collapsed-hidden">
                            <abbr title="Source Lot Ownership Percentage">Src<br>Own %</abbr>
                        </th>
                        <th scope="col" class="right src-total-weight collapsed-hidden">
                            <abbr title="Source Total Weight">Src<br>Tot Wt</abbr>
                        </th>
                        <th scope="col" class="right src-average-weight collapsed-hidden">
                            <abbr title="Source Average Weight">Src<br>Avg</abbr>
                        </th>
                        <th scope="col" class="right src-value collapsed-hidden">
                            <abbr title="Source Total Value">Src<br>Value</abbr>
                        </th>
                        <th scope="col" class="right src-unit collapsed-hidden">
                            <abbr title="Source Total Value/cwt">Src/cwt</abbr>
                        </th>
                        <th scope="col" class="left dest-lot collapsed-hidden">
                            <abbr title="Destination Lot">Dest Lot</abbr>
                        </th>
                        <th scope="col" class="right dest-owner-percentage collapsed-hidden">
                            <abbr title="Destination Lot Ownership Percentage">Dest<br>Own %</abbr>
                        </th>
                        <th scope="col" class="right dest-total-weight collapsed-hidden">
                            <abbr title="Destination Total Weight">Dest<br>Tot Wt</abbr>
                        </th>
                        <th scope="col" class="right dest-average-weight collapsed-hidden">
                            <abbr title="Destination Average Weight">Dest<br>Avg</abbr>
                        </th>
                        <th scope="col" class="right dest-value collapsed-hidden">
                            <abbr title="Destination Total Value">Dest<br>Value</abbr>
                        </th>
                        <th scope="col" class="right dest-unit collapsed-hidden">
                            <abbr title="Destination Total Value/cwt">Dest/cwt</abbr>
                        </th>
                        <th scope="col" class="left visual-tag collapsed-hidden">
                            <abbr title="Visual Tag">Vis Tag</abbr>
                        </th>
                        <th scope="col" class="left rfid collapsed-hidden">
                            RFID
                        </th>
                        <th scope="col" class="left uhf collapsed-hidden">
                            UHF
                        </th>
                        <th scope="col" class="left memo collapsed-hidden">
                            Memo
                        </th>
                    </tr>
                </thead>
                <tbody>
                    ${props.data.map((row) => this.locationResultsRow(row)).join('')}
                </tbody>
                <tfoot>
                    <tr>
                        <th scope="row" class="total collapsed-hidden">Total</th>
                        <td class="footer-total">${displayNumber(headTotal, 0)}</td>
                        <td colspan="19" class="collapsed-hidden">&nbsp;</td>
                    </tr>
                </tfoot>
                </table>
                </div>
    </div>
  </div>
        `
    }

    locationResultsRow(props) {
        const head = this.getNumber(props, this.headKeys())

        return `
            <tr class="report-data--row">
                <td class="center date">
                    ${this.displayDate(this.getRawValue(props, this.dateKeys()))}
                </td>
                <td class="right head">
                    ${displayNumber(head, 0)}
                </td>
                <td class="left sex">
                    ${this.getValue(props, ['SEX'])}
                </td>
                <td class="right dof">
                    ${displayNumber(this.getNumber(props, ['DOF']), 0)}
                </td>
                <td class="left cause">
                    ${this.getValue(props, ['CAUSE'])}
                </td>
                <td class="left src-lot">
                    ${this.getValue(props, this.srcLotKeys())}
                </td>
                <td class="right src-owner-percentage">
                    ${displayNumber(this.getNumber(props, this.srcOwnerPctKeys()), 2)}
                </td>
                <td class="right src-total-weight">
                    ${displayNumber(this.getNumber(props, ['SOURCE_DIST_OWN_TOTAL_WEIGHT', 'SRC_TOTAL_WEIGHT', 'SRC_TOT_WT', 'SRC_LOT_OWNER_WT']), 0)}
                </td>
                <td class="right src-average-weight">
                    ${displayNumber(this.getNumber(props, ['SOURCE_AVG_WEIGHT', 'SRC_AVG_WEIGHT', 'SRC_AVG', 'SRC_LOT_OWNER_WT_AVG']), 0)}
                </td>
                <td class="right src-value">
                    ${displayNumber(this.getNumber(props, ['SOURCE_DIST_OWN_TOTAL_VALUE', 'SRC_VALUE', 'SRC_LOT_OWNER_VALUE']), 2)}
                </td>
                <td class="right src-unit">
                    ${displayNumber(this.getNumber(props, ['SOURCE_TOTAL_VALUE_PER_UNIT', 'SRC_CWT', 'SRC_UNIT', 'SRC_LOT_OWNER_VALUE_PCT']), 2)}
                </td>
                <td class="left dest-lot">
                    ${this.getValue(props, this.destLotKeys())}
                </td>
                <td class="right dest-owner-percentage">
                    ${displayNumber(this.getNumber(props, this.destOwnerPctKeys()), 2)}
                </td>
                <td class="right dest-total-weight">
                    ${displayNumber(this.getNumber(props, ['DEST_DIST_OWN_TOTAL_WEIGHT', 'DEST_TOTAL_WEIGHT', 'DEST_TOT_WT', 'DST_LOT_OWNER_WT', 'DEST_LOT_OWNER_WT']), 0)}
                </td>
                <td class="right dest-average-weight">
                    ${displayNumber(this.getNumber(props, ['DEST_AVG_WEIGHT', 'DEST_AVG', 'DST_LOT_OWNER_WT_AVG', 'DEST_LOT_OWNER_WT_AVG']), 0)}
                </td>
                <td class="right dest-value">
                    ${displayNumber(this.getNumber(props, ['DEST_DIST_OWN_TOTAL_VALUE', 'DEST_VALUE', 'DST_LOT_OWNER_VALUE', 'DEST_LOT_OWNER_VALUE']), 2)}
                </td>
                <td class="right dest-unit">
                    ${displayNumber(this.getNumber(props, ['DEST_TOTAL_VALUE_PER_UNIT', 'DEST_CWT', 'DEST_UNIT', 'DST_LOT_OWNER_VALUE_PCT', 'DEST_LOT_OWNER_VALUE_PCT']), 2)}
                </td>
                <td class="left visual-tag">
                    ${this.displayAnimalIdentifier(props, ['VID', 'VISUAL_TAG', 'TAG'], head)}
                </td>
                <td class="left rfid">
                    ${this.displayAnimalIdentifier(props, ['RFID_TAG', 'RFID'], head)}
                </td>
                <td class="left uhf">
                    ${this.displayAnimalIdentifier(props, ['UHF_TAG', 'UHF'], head)}
                </td>
                <td class="left memo">
                    ${this.getValue(props, ['MEMO'])}
                </td>
            </tr>
        `
    }

    displayAnimalIdentifier(props, keys, head) {
        if (head > 1) return 'Multiple'

        return this.getValue(props, keys)
    }

    displayDate(value) {
        if (value === '' || value === undefined || value === null) return ''

        try {
            return formatForTimezone(value, 'MM/dd/yy')
        } catch (error) {
            return ''
        }
    }

    toSortableDate(value) {
        if (value === '' || value === undefined || value === null) return 0
        if (typeof value === 'number') return value

        const parsed = Date.parse(value)
        return Number.isNaN(parsed) ? 0 : parsed
    }

    dateKeys() {
        return ['EVENT_DATE', 'TRANSFER_DATE', 'DATE', 'CT_EVENT_DATE']
    }

    headKeys() {
        return ['HEAD', 'TRANSFER_HEAD']
    }

    srcLotKeys() {
        return ['SOURCE_LOT', 'SRC_LOT', 'LOT_SRC']
    }

    destLotKeys() {
        return ['DESTINATION_LOT', 'DEST_LOT', 'LOT_DST', 'DST_LOT']
    }

    srcOwnerPctKeys() {
        return ['SOURCE_DIST_OWNER_PERCENT', 'SRC_OWNER_PCT', 'SRC_OWN_PCT', 'SRC_OWNERSHIP_PCT', 'SRC_OWNERSHIP_PERCENT', 'SRC_OWNER_PERCENT', 'OWNER_PCT']
    }

    destOwnerPctKeys() {
        return ['DEST_DIST_OWN_PERCENT', 'DEST_OWNER_PCT', 'DEST_OWN_PCT', 'DEST_OWNERSHIP_PCT', 'DEST_OWNERSHIP_PERCENT', 'DEST_OWNER_PERCENT', 'DST_OWNER_PCT']
    }

    eventIdKeys() {
        return ['LT_CATTLE_EVENT_ID', 'LT_CATTLEEVENTSID', 'EVENT_ID', 'EVENT_IDS', 'CATTLE_EVENT_ID']
    }

    getNumber(props, keys) {
        const value = Number(this.getValue(props, keys, 0))

        return Number.isNaN(value) ? 0 : value
    }

    getRawValue(props, keys, defaultValue = '') {
        const actualKey = this.findKey(props, keys)

        return actualKey ? props[actualKey] : defaultValue
    }

    getValue(props, keys, defaultValue = '') {
        const value = this.getRawValue(props, keys, defaultValue)

        return `${value}`.trim()
    }

    findKey(props, keys) {
        if (props == null) return undefined

        const actualKeys = Object.keys(props)
        const lookup = actualKeys.reduce((acc, key) => {
            acc[key.toUpperCase()] = key
            return acc
        }, {})

        const match = keys
            .map((candidate) => lookup[candidate.toUpperCase()])
            .find((key) => key !== undefined && props[key] !== undefined && props[key] !== null)

        return match
    }

    connectedCallback() {}

    render() {}
}

customElements.define('mt-original-lot-transfers-report', MTOriginalLotTransfersReport)
